/** HTTP statuses that often indicate a cold start or temporary backend unavailability. */
export const TRANSIENT_HTTP_STATUSES = [502, 503, 504];

/**
 * @param {Response} response
 * @param {number[]} retryOnStatuses
 * @returns {boolean}
 */
export function isTransientHttpStatus(response, retryOnStatuses = TRANSIENT_HTTP_STATUSES) {
  return retryOnStatuses.includes(response.status);
}

/**
 * @param {Response | null} response
 * @param {number} attempt
 * @param {{ baseDelayMs: number; maxDelayMs: number }} delays
 * @returns {number}
 */
export function computeTransientRetryDelayMs(
  response,
  attempt,
  { baseDelayMs = 400, maxDelayMs = 3000 } = {},
) {
  const retryAfterHeader = response?.headers?.get?.("Retry-After");
  if (retryAfterHeader) {
    const seconds = Number.parseInt(retryAfterHeader, 10);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(maxDelayMs, Math.max(baseDelayMs, seconds * 1000));
    }
  }
  return Math.min(maxDelayMs, baseDelayMs * 2 ** Math.max(0, attempt - 1));
}

/**
 * fetch with automatic retries on transient gateway / availability responses.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {{
 *   maxAttempts?: number;
 *   baseDelayMs?: number;
 *   maxDelayMs?: number;
 *   retryOnStatuses?: number[];
 * }} [config]
 * @returns {Promise<Response>}
 */
export async function fetchWithTransientRetry(url, options = {}, config = {}) {
  const {
    maxAttempts = 3,
    baseDelayMs = 400,
    maxDelayMs = 3000,
    retryOnStatuses = TRANSIENT_HTTP_STATUSES,
  } = config;

  const signal = options.signal;
  let lastResponse = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    const response = await fetch(url, options);
    lastResponse = response;

    if (!isTransientHttpStatus(response, retryOnStatuses) || attempt === maxAttempts) {
      return response;
    }

    const delayMs = computeTransientRetryDelayMs(response, attempt, {
      baseDelayMs,
      maxDelayMs,
    });
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  return lastResponse;
}
